import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ScatterChart, Scatter, ResponsiveContainer, Cell, ReferenceLine, ZAxis,
} from 'recharts';

const COLORS = ['#26B99A', '#3498DB', '#9B59B6', '#95a5a6'];
const FIELD_FLAG_COLORS = {
  bottleneck: '#E74C3C',
  unused: '#95a5a6',
  default: '#3498DB',
};

function median(values) {
  const nums = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!nums.length) return 0;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

function StackedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="match-chart-tooltip">
      <div><strong>{row.fullName || label}</strong></div>
      {row.sigma != null && <div>Σ: {Number(row.sigma).toFixed(3)}</div>}
      {payload.map((entry) => (
        <div key={entry.dataKey}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(3) : entry.value}
        </div>
      ))}
    </div>
  );
}

function ScatterTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="match-chart-tooltip">
      <div><strong>{row.name}</strong></div>
      <div>L: {Number(row.L).toFixed(3)}</div>
      <div>Q: {Number(row.Q).toFixed(3)}</div>
    </div>
  );
}

function FieldScatterTooltip({ active, payload, labels = {} }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="match-chart-tooltip">
      <div><strong>{row.name_et}</strong></div>
      <div>{labels.projects || 'Projects'}: {row.y ?? 0}</div>
      <div>{labels.lawyers || 'Lawyers'}: {row.lawyerCount ?? 0}</div>
      <div>{labels.expertise || 'Expertise'}: {row.expertiseCount ?? 0}</div>
      {row.demandSupplyRatio != null && (
        <div>{labels.ratio || 'Demand/supply'}: {Number(row.demandSupplyRatio).toFixed(2)}</div>
      )}
      {row.flag && <div>{labels.flag || 'Flag'}: {row.flag}</div>}
    </div>
  );
}

export function ComponentStackedBar({ data, labels = {} }) {
  if (!data || !data.length) return null;
  const series = [
    { key: 'wE_E', name: labels.expertise || 'Ekspertiis', color: COLORS[0] },
    { key: 'wR_R', name: labels.rating || 'Hinne', color: COLORS[1] },
    { key: 'wS_S', name: labels.speed || 'Kiirus', color: COLORS[2] },
    { key: 'loss', name: labels.loss || 'Koormuse ja SLA kadu', color: COLORS[3] },
  ];
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 1]} />
        <Tooltip content={<StackedTooltip />} />
        <Legend />
        {series.map((s) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            stackId={s.key === 'loss' ? 'loss' : 'q'}
            fill={s.color}
            name={s.name}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function QvsLScatter({ data, theta, axisLabels = {} }) {
  if (!data || !data.length) return null;
  const xLabel = axisLabels.load || 'Koormus (L)';
  const yLabel = axisLabels.quality || 'Kvaliteet (Q)';
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="L"
          name={xLabel}
          domain={[0, 'auto']}
          label={{ value: xLabel, position: 'insideBottom', offset: -5 }}
        />
        <YAxis
          type="number"
          dataKey="Q"
          name={yLabel}
          domain={[0, 1]}
          label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
        />
        {theta != null && (
          <ReferenceLine x={theta} stroke="#E67E22" strokeDasharray="4 4" label={`θ=${theta}`} />
        )}
        <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: '3 3' }} />
        <Scatter name="Lawyers" data={data} fill="#3498DB" />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export function FieldDemandSupplyScatter({
  data,
  supplyMode = 'expertise',
  fieldWindow = '90d',
  axisLabels = {},
  tooltipLabels = {},
}) {
  const points = useMemo(() => {
    if (!data?.length) return [];
    const supplyKey = supplyMode === 'lawyers' ? 'lawyerCount' : 'expertiseCount';
    const zKey = supplyMode === 'lawyers' ? 'expertiseCount' : 'lawyerCount';
    const projectKey = fieldWindow === 'all' ? 'countAll' : 'count90d';
    return data
      .filter((row) => (row.lawyerCount || 0) > 0
        || (row.expertiseCount || 0) > 0
        || (row[projectKey] || 0) > 0)
      .map((row) => ({
        ...row,
        x: row[supplyKey] || 0,
        y: row[projectKey] || 0,
        z: Math.max(row[zKey] || 0, 1),
      }));
  }, [data, supplyMode, fieldWindow]);

  const { medianX, medianY } = useMemo(() => ({
    medianX: median(points.map((p) => p.x)),
    medianY: median(points.map((p) => p.y)),
  }), [points]);

  if (!points.length) return null;

  const xLabel = supplyMode === 'lawyers'
    ? (axisLabels.supplyLawyers || 'Lawyers')
    : (axisLabels.supplyExpertise || 'Expertise');
  const yLabel = axisLabels.demand || 'Projects';

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="x"
          name={xLabel}
          domain={[0, 'auto']}
          allowDecimals={false}
          label={{ value: xLabel, position: 'insideBottom', offset: -10 }}
        />
        <YAxis
          type="number"
          dataKey="y"
          name={yLabel}
          domain={[0, 'auto']}
          allowDecimals={false}
          label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
        />
        <ZAxis type="number" dataKey="z" range={[40, 400]} />
        <ReferenceLine x={medianX} stroke="#BDC3C7" strokeDasharray="4 4" />
        <ReferenceLine y={medianY} stroke="#BDC3C7" strokeDasharray="4 4" />
        <Tooltip
          content={<FieldScatterTooltip labels={tooltipLabels} />}
          cursor={{ strokeDasharray: '3 3' }}
        />
        <Scatter name="Fields" data={points}>
          {points.map((entry) => (
            <Cell
              key={entry.field_id}
              fill={FIELD_FLAG_COLORS[entry.flag] || FIELD_FLAG_COLORS.default}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export function HistogramBar({ data, title, onBucketClick, selectedBucket }) {
  if (!data || !data.length) return null;
  return (
    <div>
      {title && <h4>{title}</h4>}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar
            dataKey="count"
            fill="#26B99A"
            cursor={onBucketClick ? 'pointer' : 'default'}
            onClick={(entry) => onBucketClick && onBucketClick(entry?.payload?.bucket)}
          >
            {data.map((d, i) => (
              <Cell
                key={d.bucket}
                fill={selectedBucket === d.bucket ? '#1a7f5a' : COLORS[i % COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function fmtNum(v, d = 3) {
  if (v == null || Number.isNaN(v)) return '—';
  const decimals = typeof d === 'number' ? d : 3;
  return Number(v).toFixed(decimals);
}

export function fmtInt(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return String(Math.round(Number(v)));
}

export function fmtScore(v) {
  return fmtNum(v, 3);
}

export function fmtPct(v) {
  if (v == null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
}
